import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type { BroadcastResponse } from '@/types/broadcast.ts';
import { BroadcastCounts } from './BroadcastCounts.tsx';

export interface SendResultDialogProps {
  /** The answer to the last send; null when none has been made. */
  result: BroadcastResponse | null;
  onClose: () => void;
}

/** The title says the outcome before the numbers do. */
function resultTitle(result: BroadcastResponse): string {
  if (result.failed === 0) return 'Broadcast Sent Successfully';
  if (result.sent > 0) return 'Broadcast Partially Succeeded';
  return 'Broadcast Failed';
}

function resultSummary(result: BroadcastResponse): string {
  if (result.failed === 0) {
    return `Successfully sent broadcast to ${result.sent} user${result.sent !== 1 ? 's' : ''}.`;
  }
  if (result.sent === 0) {
    return 'Broadcast failed. No messages were sent.';
  }
  return `Broadcast partially succeeded. ${result.sent} sent, ${result.failed} failed.`;
}

/**
 * How the send went, once it has gone.
 *
 * It always opens, success or not: a broadcast cannot be taken back, so what
 * actually happened is the last thing the sender gets to see about it.
 */
export function SendResultDialog({ result, onClose }: SendResultDialogProps) {
  return (
    <Dialog open={result !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size='lg'>
        {result !== null && (
          <>
            <DialogHeader>
              <DialogTitle>{resultTitle(result)}</DialogTitle>
            </DialogHeader>

            <div className='flex flex-col gap-4 px-5 py-4'>
              <p
                role='status'
                className={
                  result.failed === 0 ? 'text-sm text-ink' : 'text-sm text-danger'
                }
              >
                {resultSummary(result)}
              </p>

              <BroadcastCounts
                total={result.total}
                sent={result.sent}
                failed={result.failed}
              />

              {result.errors.length > 0 && (
                <div className='flex flex-col gap-1.5'>
                  <h4 className='text-sm font-semibold text-ink'>Error Details:</h4>
                  <ul className='flex flex-col gap-1'>
                    {result.errors.map((error, index) => (
                      <li
                        /* Two identical lines can both be true; only the order tells them apart. */
                        key={`${error}-${index}`}
                        className='rounded-md bg-fill-faint px-2.5 py-1.5 text-sm text-ink-secondary'
                      >
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button size='sm' onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
